import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/brand/Logo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Check, X, Clock as ClockIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatMinutes, formatDecimalHours, formatWeekLabel } from "@/lib/timeFormat";
import { useTheme } from "@/components/ThemeProvider";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Bare axios — no auth interceptors, no token needed for public magic endpoints.
const magic = axios.create({ baseURL: `${API_BASE}/api/magic`, timeout: 20000 });

export default function MagicApprovePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [actedOn, setActedOn] = useState({});
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) {
        setState({ loading: false, error: "expired", data: null });
        return;
      }
      try {
        const { data } = await magic.get("/approve", { params: { token } });
        setState({ loading: false, error: null, data });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setState({ loading: false, error: detail || "expired", data: null });
      }
    })();
  }, [token]);

  const sheets = state.data?.sheets || [];
  const outstanding = useMemo(
    () => sheets.filter((s) => !actedOn[s.id] && s.status === "submitted"),
    [sheets, actedOn]
  );

  const approveOne = async (ts_id) => {
    setBusy(true);
    try {
      const { data } = await magic.post(`/approve/${ts_id}`, { token });
      setActedOn((a) => ({ ...a, [ts_id]: { action: "approved", status: data.status } }));
      toast.success("Approved");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed";
      toast.error(String(detail));
    } finally { setBusy(false); }
  };

  const openReject = (ids) => {
    setPendingAction({ ids });
    setRejectReason("");
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectReason.trim()) return;
    setBusy(true);
    try {
      if (pendingAction.ids.length === 1) {
        const { data } = await magic.post(`/reject/${pendingAction.ids[0]}`, { token, reason: rejectReason });
        setActedOn((a) => ({ ...a, [pendingAction.ids[0]]: { action: "rejected", status: data.status } }));
        toast.success("Rejected");
      } else {
        const { data } = await magic.post("/reject/bulk", { token, timesheet_ids: pendingAction.ids, reason: rejectReason });
        const next = { ...actedOn };
        for (const r of data.results) {
          if (r.ok) next[r.id] = { action: "rejected", status: r.status };
        }
        setActedOn(next);
        toast.success(`${data.rejected} rejected`);
      }
      setRejectOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed";
      toast.error(String(detail));
    } finally { setBusy(false); }
  };

  const approveAll = async () => {
    if (!outstanding.length) return;
    setBusy(true);
    try {
      const { data } = await magic.post("/approve/bulk", {
        token, timesheet_ids: outstanding.map((s) => s.id),
      });
      const next = { ...actedOn };
      for (const r of data.results) {
        if (r.ok) next[r.id] = { action: "approved", status: r.status };
      }
      setActedOn(next);
      toast.success(`${data.approved} approved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Logo variant="dark" />
          <div className="text-sm text-primary-foreground/70">
            {state.data && `Approvals for ${state.data.tenant.name}`}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        {state.loading && (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading link…</div>
        )}
        {!state.loading && state.error && (
          <ExpiredLinkView />
        )}
        {!state.loading && !state.error && state.data && (
          <div className="space-y-6" data-testid="magic-approve-page">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Signed in via one-time link</div>
                <h1 className="mt-1 font-display text-3xl font-semibold text-primary">
                  Welcome, {state.data.approver.full_name || state.data.approver.email}
                </h1>
                <p className="mt-2 text-muted-foreground">
                  You have <strong>{outstanding.length}</strong> pending timesheet{outstanding.length === 1 ? "" : "s"} for <strong>{formatWeekLabel(state.data.period_start_local, state.data.sheets[0]?.period_end_local || "")}</strong>.
                </p>
                <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span>Link expires {new Date(state.data.expires_at).toLocaleDateString()}</span>
                </div>
              </div>
              {outstanding.length > 1 && (
                <div className="flex gap-2">
                  <Button onClick={approveAll} disabled={busy} data-testid="magic-approve-all-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve all
                  </Button>
                  <Button variant="destructive" onClick={() => openReject(outstanding.map((s) => s.id))} disabled={busy} data-testid="magic-reject-all-btn">
                    <X className="h-4 w-4" /> Reject all
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {sheets.map((s) => {
                const acted = actedOn[s.id];
                const effectiveStatus = acted?.status || s.status;
                const decided = effectiveStatus !== "submitted";
                return (
                  <Card key={s.id} className={decided ? "opacity-70" : "card-elevate"} data-testid={`magic-sheet-${s.id}`}>
                    <CardContent className="pt-6">
                      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <div className="font-display text-xl font-semibold text-primary">{s.worker_name}</div>
                          <div className="mt-1 text-sm text-muted-foreground">{formatWeekLabel(s.period_start_local, s.period_end_local)}</div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="font-display text-2xl font-semibold tabular-nums text-primary">
                            {formatMinutes(s.total_minutes)} <span className="text-sm font-normal text-muted-foreground">({formatDecimalHours(s.total_minutes)}h)</span>
                          </div>
                          <MagicStatusPill status={effectiveStatus} />
                        </div>
                      </div>

                      <div className="grid grid-cols-7 gap-2">
                        {["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"].map((label, i) => {
                          const d = shiftDate(s.period_start_local, i);
                          const mins = (s.hours_by_day || {})[d] || 0;
                          return (
                            <div key={d} className={`rounded-lg border p-2 text-center ${mins > 0 ? "" : "opacity-40"}`}>
                              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                              <div className="mt-1 tabular-nums text-sm">{mins ? formatMinutes(mins) : "—"}</div>
                            </div>
                          );
                        })}
                      </div>

                      {!decided && (
                        <div className="mt-4 flex flex-wrap justify-end gap-2">
                          <Button size="sm" onClick={() => approveOne(s.id)} disabled={busy} data-testid={`magic-approve-${s.id}`}>
                            <Check className="h-4 w-4" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => openReject([s.id])} disabled={busy} data-testid={`magic-reject-${s.id}`}>
                            <X className="h-4 w-4" /> Reject
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {sheets.length === 0 && (
                <Alert>
                  <AlertDescription>Nothing to review — your bundle is empty.</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        )}
      </main>

      <Dialog open={rejectOpen} onOpenChange={(v) => !v && setRejectOpen(false)}>
        <DialogContent data-testid="magic-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {pendingAction?.ids?.length === 1 ? "timesheet" : `${pendingAction?.ids?.length} timesheets`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Reason (required)</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={4} placeholder="Explain what needs to change…" data-testid="magic-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmReject} disabled={busy || !rejectReason.trim()} data-testid="magic-reject-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExpiredLinkView() {
  const { theme } = useTheme();
  const brand = theme?.product_display_name || "Quanta";
  return (
    <Card className="mx-auto max-w-md" data-testid="magic-expired">
      <CardContent className="pt-8 text-center">
        <ClockIcon className="mx-auto h-8 w-8 text-muted-foreground" />
        <h2 className="mt-4 font-display text-2xl font-semibold text-primary">This link has expired</h2>
        <p className="mt-2 text-muted-foreground">Please log in to {brand} to review pending timesheets.</p>
        <div className="mt-6">
          <Button asChild><Link to="/login">Sign in</Link></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MagicStatusPill({ status }) {
  const map = {
    submitted: "bg-blue-500/15 text-blue-700",
    approved: "bg-success/15 text-success",
    rejected: "bg-destructive/15 text-destructive",
    revised: "bg-amber-500/15 text-amber-700",
    draft: "bg-muted text-muted-foreground",
  };
  return <Badge className={`rounded-full capitalize ${map[status] || map.draft}`}>{status}</Badge>;
}

function shiftDate(sat_iso, offset_days) {
  const [y, m, d] = sat_iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + offset_days));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
